import { gql, useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import { Flex } from 'rebass';
import PropTypes from 'prop-types';
import { Input, Label } from '@rebass/forms';
import useNotify from '../../../../hooks/useNotify';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import Button from '../../../../components/Button/Button';
import Modal, { ModalBody, ModalFooter } from '../../../../components/Modal';
import UserPicker from '../../../../components/User/UserPicker';

type AssignJobModalProps = {
  onClose: (actionTaken?: boolean) => void;
  action: 'ASSIGN';
  assignmentIds: string[] | readonly string[];
};

const ASSIGN_JOBS = gql`
  mutation AssignInboundAssignments(
    $assignmentIds: [ID!]!
    $vendorProfileId: ID!
    $dueDate: String!
  ) {
    assignInboundAssignments(
      assignmentIds: $assignmentIds
      vendorProfileId: $vendorProfileId
      dueDate: $dueDate
    ) {
      id
      status
      child {
        id
        status
        vendorProfile {
          id
          name
        }
        due
      }
    }
  }
`;

const AssignModal: React.FC<AssignJobModalProps> = ({
  action,
  onClose,
  assignmentIds,
}) => {
  const { showError, showSuccess } = useNotify();

  const onCancel = useCallback(() => onClose(), []);
  const onActionComplete = useCallback(() => {
    showSuccess(
      `${assignmentIds.length} job${
        assignmentIds.length > 1 ? 's' : ''
      } assigned`
    );
    onClose(true);
  }, []);

  const [assignJobs, { loading: mutationLoading }] = useMutation(ASSIGN_JOBS, {
    onCompleted: onActionComplete,
    onError: useCallback((error) => {
      showError(
        `Error Assigning Job${assignmentIds.length > 1 ? 's' : ''}`,
        error
      );
    }, []),
  });

  const [vendorProfileId, setVendorProfileId] = useState<
    string | null | undefined
  >();
  const onSelectSpecialist = useCallback(
    (e: any) => {
      setVendorProfileId(e.target.value);
    },
    [setVendorProfileId]
  );

  const [dueDate, setDueDate] = useState<string>('');
  const onDueDateChange = useCallback(
    (e: any) => {
      setDueDate(e.target.value);
    },
    [setDueDate]
  );

  return (
    <Modal
      title={`${constantToLabel(action)} ${
        assignmentIds.length > 1 ? assignmentIds.length : ''
      } Job${assignmentIds.length > 1 ? 's' : ''}`}
      onClose={onCancel}
    >
      <ModalBody>
        <Flex p={3} flexDirection="column">
          <Label htmlFor="specialist">Select Specialist</Label>
          <UserPicker
            autoFocus
            companies={false}
            userId={vendorProfileId}
            onChange={(user) => {
              setVendorProfileId(user?.profileId);
            }}
          />
          <Label htmlFor="date">Optional Due Date</Label>
          <Input id="date" name="date" type="date" onChange={onDueDateChange} />
          {mutationLoading && <p>Loading...</p>}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          disabled={mutationLoading}
          onClick={() => {
            assignJobs({
              variables: {
                assignmentIds,
                vendorProfileId: vendorProfileId,
                dueDate,
              },
            });
          }}
        >
          Assign
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AssignModal.propTypes = {
  onClose: PropTypes.any,
  assignmentIds: PropTypes.any.isRequired,
};

export default AssignModal;
